/**
 * Lazy loader for Agora RTC SDK and extensions.
 * This module provides on-demand loading of Agora SDK to reduce initial bundle size.
 * The SDK is only loaded when getAgoraRTC() is called, typically when entering
 * broadcast, tech setup, or discussion room routes.
 *
 * Why lazy loading? CloudFront only compresses files up to 10MB. The Agora SDK is ~3.6MB,
 * and including it in the main bundle pushed it over 12MB, causing uncompressed transfers.
 * By lazy loading, we keep the main bundle under 10MB and only load Agora when needed.
 */
import type { AIDenoiserExtension as AIDenoiserExtensionType } from 'agora-extension-ai-denoiser';
import type VirtualBackgroundExtensionType from 'agora-extension-virtual-background';
import type AgoraRTCType from 'agora-rtc-sdk-ng';

import { getStaticAssetsUrl } from '@/constants';

let agoraRTCInstance: typeof AgoraRTCType | null = null;
let denoiserInstance: AIDenoiserExtensionType | null = null;
let virtualBackgroundInstance: InstanceType<typeof VirtualBackgroundExtensionType> | null = null;
let loadingPromise: Promise<typeof AgoraRTCType> | null = null;

/**
 * Lazily loads the Agora RTC SDK and registers extensions.
 * Uses singleton pattern - subsequent calls return the same instance.
 * On load failure, resets state to allow retry on next call.
 */
export async function getAgoraRTC(): Promise<typeof AgoraRTCType> {
  if (agoraRTCInstance) {
    return agoraRTCInstance;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = (async () => {
    try {
      const [{ default: AgoraRTC }, { AIDenoiserExtension }, { default: VirtualBackgroundExtension }] =
        await Promise.all([
          import('agora-rtc-sdk-ng'),
          import('agora-extension-ai-denoiser'),
          import('agora-extension-virtual-background')
        ]);

      // Use instance check as source of truth to prevent duplicate registrations
      if (!denoiserInstance) {
        denoiserInstance = new AIDenoiserExtension({
          assetsPath: getStaticAssetsUrl('agoraExternal')
        });
        virtualBackgroundInstance = new VirtualBackgroundExtension();
        AgoraRTC.registerExtensions([virtualBackgroundInstance, denoiserInstance]);
      }

      agoraRTCInstance = AgoraRTC;
      return AgoraRTC;
    } catch (error) {
      // Reset loading promise so subsequent calls can retry
      loadingPromise = null;
      console.error('[AgoraLoader] Failed to load Agora SDK:', error);
      throw error;
    }
  })();

  return loadingPromise;
}

/**
 * Returns the Agora RTC SDK instance synchronously if already loaded.
 * Returns null if the SDK hasn't been loaded yet.
 */
export function getAgoraRTCSync(): typeof AgoraRTCType | null {
  return agoraRTCInstance;
}

/**
 * Returns whether the Agora SDK has been loaded.
 */
export function isAgoraLoaded(): boolean {
  return agoraRTCInstance !== null;
}

/**
 * Returns the AI Denoiser extension instance.
 * Must call getAgoraRTC() first to ensure extensions are loaded.
 */
export function getDenoiserExtension(): AIDenoiserExtensionType | null {
  return denoiserInstance;
}

/**
 * Returns the Virtual Background extension instance.
 * Must call getAgoraRTC() first to ensure extensions are loaded.
 */
export function getVirtualBackgroundExtension(): InstanceType<typeof VirtualBackgroundExtensionType> | null {
  return virtualBackgroundInstance;
}

/**
 * Checks if AI Denoiser is compatible with the current browser.
 * Loads the SDK if not already loaded.
 */
export async function checkDenoiserCompatibility(): Promise<boolean> {
  try {
    await getAgoraRTC();
    if (denoiserInstance) {
      return denoiserInstance.checkCompatibility();
    }
    return false;
  } catch (error) {
    console.error('Error checking denoiser compatibility:', error);
    return false;
  }
}

/**
 * AI Denoiser processor level constants.
 * These mirror the AIDenoiserProcessorLevel enum values to avoid importing the module.
 */
export const DenoiserLevel = {
  SOFT: 'SOFT' as const,
  AGGRESSIVE: 'AGGRESSIVE' as const
} as const;

export type DenoiserLevelType = (typeof DenoiserLevel)[keyof typeof DenoiserLevel];
